import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/;
const childPath = (path, key, isArray) =>
    isArray ? `${path}[${key}]` : IDENTIFIER.test(key) ? `${path}.${key}` : `${path}['${String(key).replace(/'/g, "\\'")}']`;

const typeLabel = (value) => {
    if (Array.isArray(value)) return `array(${value.length})`;
    if (value === null) return "null";
    if (typeof value === "object") return `object(${Object.keys(value).length})`;
    return typeof value;
};

const Preview = ({ value }) => {
    if (typeof value === "string") return <span className="text-green-700 break-all">"{value.length > 80 ? `${value.slice(0, 80)}…` : value}"</span>;
    if (typeof value === "number") return <span className="text-blue-700">{value}</span>;
    if (typeof value === "boolean") return <span className="text-purple-700">{String(value)}</span>;
    if (value === null) return <span className="text-gray-400">null</span>;
    return null;
};

const Node = ({ name, value, path, depth, onPick, schema, expandedByDefault }) => {
    const [open, setOpen] = useState(expandedByDefault);
    const isContainer = value !== null && typeof value === "object";
    const entries = isContainer ? (Array.isArray(value) ? value.map((v, i) => [i, v]) : Object.entries(value)) : [];
    const stats = schema?.get(path.replace(/\[\d+\]/g, "[*]"));

    return (
        <div style={{ paddingLeft: depth ? 14 : 0 }}>
            <div className="flex items-center gap-1 text-xs font-mono leading-6 group">
                {isContainer ? (
                    <button type="button" className="text-gray-500" onClick={() => setOpen(!open)}>
                        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    </button>
                ) : (
                    <span className="w-3" />
                )}
                <button
                    type="button"
                    title={`Use ${path}`}
                    className="text-gray-800 hover:text-primary hover:underline"
                    onClick={() => onPick?.(path, value)}
                >
                    {name}
                </button>
                <span className="text-gray-400">{typeLabel(value)}</span>
                <Preview value={value} />
                {stats && <span className="text-gray-300 opacity-0 group-hover:opacity-100">{stats.count}× {stats.types.join("|")}</span>}
            </div>
            {open && isContainer && (
                <div>
                    {entries.map(([key, child]) =>
                        typeof child === "string" && child.startsWith("… ") && child.endsWith(" more") ? (
                            <p key={key} className="text-xs text-gray-400 pl-6">{child}</p>
                        ) : (
                            <Node
                                key={key}
                                name={Array.isArray(value) ? `[${key}]` : key}
                                value={child}
                                path={childPath(path, key, Array.isArray(value))}
                                depth={depth + 1}
                                onPick={onPick}
                                schema={schema}
                                expandedByDefault={depth < 1}
                            />
                        )
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * JSON tree of the last test response (trimmed by the server). Clicking a node
 * passes its concrete path (e.g. $.products[0].title) to onPick.
 */
const ResponseExplorer = ({ sample, detection, onPick, pickLabel }) => {
    const schema = useMemo(() => new Map((detection?.schema || []).map((entry) => [entry.path, entry])), [detection]);
    if (sample === undefined || sample === null) {
        return <p className="text-sm text-gray-500">Test the request to explore the response.</p>;
    }
    return (
        <div className="flex flex-col gap-2">
            <p className="text-xs text-gray-500">{pickLabel || "Click a name to use its path. Long lists are cut short."}</p>
            <div className="overflow-auto max-h-[70vh] border border-gray-200 rounded-md p-2 bg-gray-50">
                <Node name="$" value={sample} path="$" depth={0} onPick={onPick} schema={schema} expandedByDefault />
            </div>
        </div>
    );
};

export default ResponseExplorer;
